import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";

export function useCompanion(code, display = false) {
  const qc = useQueryClient();
  const key = ["companion", code, display];
  const ver = useRef(0);
  const [live, setLive] = useState(false);
  const query = useQuery({ queryKey: key, retry: false, queryFn: () => api.get(`/companion/sessions/${code}`, { params: { display } }).then((r) => r.data) });
  useEffect(() => { if (query.data?.version) ver.current = query.data.version; }, [query.data]);

  useEffect(() => {
    let ws, timer, stop = false, retry = 0;
    const connect = () => {
      ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/v1/companion/ws/${code}`);
      ws.onopen = () => { setLive(true); retry = 0; };
      ws.onmessage = (e) => { const { version } = JSON.parse(e.data); if (version !== ver.current) qc.invalidateQueries({ queryKey: ["companion", code] }); };
      ws.onclose = () => { setLive(false); if (!stop) timer = setTimeout(connect, Math.min(10000, 1000 * 2 ** retry++)); };
    };
    connect();
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25000);
    return () => { stop = true; clearTimeout(timer); clearInterval(ping); ws?.close(); };
  }, [code, qc]);

  useEffect(() => {
    if (live) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/companion/sessions/${code}`, { params: { since: ver.current, display } });
        if (r.data.changed !== false) qc.setQueryData(["companion", code, display], r.data);
      } catch { /* retried on next tick */ }
    }, 2000);
    return () => clearInterval(t);
  }, [live, code, display, qc]);

  const set = (d) => qc.setQueryData(key, d);
  const call = (fn, msg) => fn().then((r) => { set(r.data); if (msg) toast.success(msg); return r.data; }).catch((err) => { toast.error(formatApiError(err)); });
  const act = (action, data = {}) => call(() => api.post(`/companion/sessions/${code}/actions`, { action, data, client_id: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`) }));
  return { s: query.data, error: query.error, loading: query.isLoading, live, act, call };
}
